import { FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { makeAutoObservable, runInAction } from 'mobx';
import { getRFQProposalChecklistURL } from 'global/checklists-helpers/RFQProposalChecklistHelper';
import { bpHistory } from 'global/bpHistory';
import { getCoveredProductLines } from 'global/services-helpers/enrollmentService';
import InitiateQuoteFlowDelegate from 'store/flows/initiate-quote/initiateQuoteFlowDelegate';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { EnrollmentPeriod, EnrollmentStage } from 'types/enrollmentService';
import { createRenewalRFQPortfolios } from 'api/operations/productServiceV2';
import { DomainProductLineItem } from './domainProductLinesDelegate';
import { RenewalActionsDelegate } from './renewalActionsDelegate';
import {
  ProposalExperience,
  ProposalExperienceAccessLimitations,
  ProposalExperienceDisabledReasons,
  PROPOSAL_EXPERIENCE_DISABLED_REASONS,
  ProposalExperiencePayload,
  ProposalExperiencePolicy,
} from './types';

export interface DomainParams {
  initiateQuoteFlowDelegate: InitiateQuoteFlowDelegate;
  getPolicy: () => ProposalExperiencePolicy;
  getPayload: () => ProposalExperiencePayload;
}

export default class ProposalExperienceStepController {
  private readonly _initiateQuoteFlowDelegate: InitiateQuoteFlowDelegate;
  private readonly _rootStore: RootStore = getRootStoreInstance();
  private readonly _getPayload: () => ProposalExperiencePayload;
  private readonly _getPolicy: () => ProposalExperiencePolicy;
  public readonly renewalActionsDelegate: RenewalActionsDelegate;

  public form: FormController<{
    proposalExperience: FormControl<string>;
  }>;

  public get isValid(): boolean {
    if (!this.form.basicFieldsValidationsPassed) {
      return false;
    }

    if (this._isRenewalEffectiveDate) {
      return this.renewalActionsDelegate.hasAvailableActions;
    }

    return true;
  }

  public get proposalExperience(): ProposalExperience | null {
    return this.form.fields.proposalExperience.value
      ? (this.form.fields.proposalExperience.value as ProposalExperience)
      : null;
  }

  private get _effectiveDate(): string {
    return this._getPayload().browsingCriteria.effectiveDate ?? '';
  }

  private get _isRenewalEffectiveDate(): boolean {
    return this._initiateQuoteFlowDelegate.isRenewalEffectiveDate(this._effectiveDate);
  }

  public get experiences(): ProposalExperience[] {
    const { newBusinessExperiences, renewalExperiences } = this._getPolicy();

    if (this._isRenewalEffectiveDate) {
      return renewalExperiences;
    }

    return newBusinessExperiences;
  }

  public get experienceAccessLimitations(): ProposalExperienceAccessLimitations {
    return this._getPolicy().getExperienceAccessLimitations?.() ?? {};
  }

  public get experienceDisabledReasons(): ProposalExperienceDisabledReasons {
    // If the policy has custom disabled reasons, use them instead of the default ones
    if (this._getPolicy().getExperienceDisabledReasons) {
      return this._getPolicy().getExperienceDisabledReasons?.() ?? {};
    }

    const isShelfAvailable = this._initiateQuoteFlowDelegate.domainProductLinesDelegate.hasQuotableProductLine;
    const isFavoriteAvailable =
      this._rootStore.domainStoreV2.isChangePlanActionAvailable(this._rootStore.userStore.userRoles) ||
      getCoveredProductLines(this._initiateQuoteFlowDelegate.enrollmentPeriods).length === 0;

    return {
      SHELF: isShelfAvailable ? null : PROPOSAL_EXPERIENCE_DISABLED_REASONS.ALL_PRODUCT_LINES_QUOTED,
      FAVORITE: isFavoriteAvailable ? null : PROPOSAL_EXPERIENCE_DISABLED_REASONS.COVERAGE_ALREADY_IN_FORCE,
      CUSTOM: null,
      RENEWAL_SHELF: null,
    };
  }

  private get _renewalEnrollmentPeriod(): EnrollmentPeriod | undefined {
    const effectiveDate = this._effectiveDate;

    return this._initiateQuoteFlowDelegate.enrollmentPeriods.find(
      (period) => period.stage === EnrollmentStage.RENEWAL && period.effectiveDate === effectiveDate,
    );
  }

  public resetStep(): void {
    this.form.resetForm();
  }

  // Creates a cart and saves the proposal. CUSTOM continues to the RFQ checklist, other experiences go to the quote checklist
  public async completeStep(): Promise<void> {
    const { proposalName, browsingCriteria } = this._getPayload();

    await this._initiateQuoteFlowDelegate.createCartAndSaveProposal({
      effectiveDate: browsingCriteria.effectiveDate ?? '',
      proposalName,
      rateSegment: this._rateSegment,
      browsingCriteria,
    });

    if (this.proposalExperience === 'RENEWAL_CUSTOM') {
      const installedProposal = this._renewalEnrollmentPeriod?.installedProposal;
      if (!installedProposal) {
        throw new Error('Period not found for renewal custom proposal.');
      }
      const initialSeedIds = installedProposal.productLines.flatMap((productLine) =>
        productLine.products.map((product) => product.carrierProductId),
      );
      const groupId = this._initiateQuoteFlowDelegate.domainData.groupId;
      await createRenewalRFQPortfolios(groupId, initialSeedIds);
    }

    runInAction(() => {
      if (this.proposalExperience === 'CUSTOM' || this.proposalExperience === 'RENEWAL_CUSTOM') {
        // The RFQ flow is currently separated from the Quote flow on the FE.
        // Legacy RFQ pages still use a dedicated RFQ checklist, while the BE serves RFQ steps as part of the Quote checklist.
        //
        // TODO:
        // - Merge the RFQ flow into the Quote checklist.
        // - Add RFQ steps to the Quote checklist helper.
        // - Remove the dedicated RFQ checklist from the FE.
        // - Replace this custom redirect with this._initiateQuoteFlowDelegate.onCompleteRedirect() once both flows are unified.
        bpHistory.push(
          getRFQProposalChecklistURL({
            periodId: this._initiateQuoteFlowDelegate.quoteData.periodId,
            cartId: this._initiateQuoteFlowDelegate.quoteData.cartId,
          }),
        );
        return;
      }

      this._initiateQuoteFlowDelegate.onCompleteRedirect();
    });
  }

  private get _rateSegment(): RateSegment {
    if (this.proposalExperience === 'RENEWAL_SHELF') {
      return this._renewalEnrollmentPeriod?.rateSegment || 'GROUP';
    }

    if (this.proposalExperience === 'CUSTOM' || this.proposalExperience === 'RENEWAL_CUSTOM') {
      return 'CUSTOM';
    }

    return 'GROUP';
  }

  // Is used by BulletTabsDelegate to apply the default experience
  public prepareStep(): void {
    if (this._initiateQuoteFlowDelegate.domainProductLinesDelegate.isLoading) {
      return;
    }

    const availableExperiences = this.experiences.filter((experience) => !this.experienceDisabledReasons[experience]);
    const currentExperience = this.form.fields.proposalExperience.value as ProposalExperience;
    const isCurrentAvailable = availableExperiences.includes(currentExperience);

    if (availableExperiences.length === 1) {
      this.form.fields.proposalExperience.overrideValue(availableExperiences[0]);
      return;
    }

    if (currentExperience && !isCurrentAvailable) {
      this.form.fields.proposalExperience.overrideValue('');
    }
  }

  constructor({ initiateQuoteFlowDelegate, getPayload, getPolicy }: DomainParams) {
    this._initiateQuoteFlowDelegate = initiateQuoteFlowDelegate;
    this._getPayload = getPayload;
    this._getPolicy = getPolicy;

    this.renewalActionsDelegate = new RenewalActionsDelegate({
      getEffectiveDate: (): string => {
        return this._renewalEnrollmentPeriod?.effectiveDate ?? '';
      },
      getInstalledProductLineIds: (): ProductLineId[] => {
        return (
          this._renewalEnrollmentPeriod?.installedProposal?.productLines.map((productLine) => productLine.id) ?? []
        );
      },
      getDomainProductLineItems: (): DomainProductLineItem[] =>
        this._initiateQuoteFlowDelegate.domainProductLinesDelegate.domainProductLineItems,
    });

    this.form = FormController.create(
      {
        proposalExperience: new FormControl<string>('', { optional: false }),
      },
      {},
    );

    makeAutoObservable(this, { renewalActionsDelegate: false }, { autoBind: true });
  }
}
